<template>
  <div class="searchResult">
    <HeadingLv1 :text="state.title" class="searchResult_title" />

    <section class="searchResult_contents">
      <Loader v-if="state.isLoading" />

      <SearchResultInformation
        class="searchResultInformation"
        v-show="state.totalCount > 0 && !state.isLoading"
        :total-count="state.totalCount"
        :page-num="state.pageNum"
        :row-cnt="state.rowCnt"
        @change-sort-options="changeSortOptions"
      />
      <HospitalsList :list="list" v-show="!state.isLoading" />
      <Pager
        v-show="!state.isLoading"
        class="pagerBlock"
        :total-count="state.totalCount"
        :page-num="state.pageNum"
        :row-cnt="state.rowCnt"
        @move-page="movePage"
      />
    </section>

    <div class="searchResult_condition">
      <SearchCondition
        :isSidebar="true"
        :class="{
          /* NOTE: state.fixedで切り替えるようになっていたが、fixedというメンバーはstateには存在しない。おそらくsticky実装が正。 */
          /* TODO: おそらく使用しないので問題なければ削除する */
          'condition-fixed': false
        }"
        :querys="currentParams"
        :keyword-only="state.showSearchConditionSm ? false : true"
        @click="state.showSearchConditionSm = false"
      />
      <div class="searchResult_ad hiddenSp" v-if="adBanners[0]">
        <AdBanner :item="adBanners[0]" ad-type="searchResult" />
      </div>
    </div>
    <div class="searchResult_ad hiddenPc" v-if="adBanners[0]">
      <AdBanner :item="adBanners[0]" ad-type="searchResult" />
    </div>

    <div
      v-if="Object.keys(internalLinks.list).length > 0"
      class="searchResult_internalLinks"
    >
      <InternalLinks :list="internalLinks.list" />
    </div>
  </div>
</template>

<script lang="ts">
import { onMounted, reactive, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SearchCondition from '@/components/block/SearchCondition.vue'
import SearchResultInformation from '@/components/block/SearchResultInformation.vue'
import HospitalsList from '@/components/block/HospitalsList.vue'
import HeadingLv1 from '@/components/elements/HeadingLv1.vue'
import Pager from '@/components/block/Pager.vue'
import Loader from '@/components/elements/Loader.vue'
import AdBanner, { AdBannerType } from '@/components/block/AdBanner.vue'
import InternalLinks, {
  InternalLinksType
} from '@/components/block/InternalLinks.vue'
import { axiosSetting } from '@/utils/axiosSetting'
import { isPC } from '@/utils/commonMethods'
import { getSearchQuery, setSearchResultTitle } from '@/utils/searchMethods'
import { setSearchConditionCookie } from '@/utils/cookie'

interface InternalLinksReqType {
  free_word?: string
  prefectures?: string[]
  hospital_class?: string[]
  designated_hospital?: string[]
  discerning_conditions?: string[]
  bonus?: boolean
  overtime_hours_mavg?: boolean
  house?: boolean
  bed_num_lo?: string
  doctor_num_lo?: string
  positions_num_lo?: string
  income_lo?: string
  shifts_num_lo?: string
}

export default {
  name: 'hospitalsList',

  components: {
    SearchCondition,
    SearchResultInformation,
    HospitalsList,
    HeadingLv1,
    Pager,
    Loader,
    AdBanner,
    InternalLinks
  },

  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const state = reactive({
      title: '',
      totalCount: 0,
      pageNum: 1,
      sortOption: 1,
      rowCnt: 15,
      showSearchConditionSm: false,
      isLoading: true
    })
    let lastScrollTop = 0
    let isBusy = false
    const list = reactive<any>([])

    // タイトルやディスクリプション設定
    const setMeta = () => {
      const result = setSearchResultTitle(
        route.path.replace('/search/hospital/list', '')
      )
      state.title = result.title

      const baseTitle =
        document.title.indexOf(' | ') > -1
          ? document.title.split(' | ')[1]
          : document.title
      document.title = result.title + ' | ' + baseTitle

      document
        .querySelector('meta[name="description"]')
        ?.setAttribute(
          'content',
          `${result.title}。出会いをつくることで、日本の医療に貢献したい。私たちはマイナビRESIDENTです。`
        )
    }

    // URLから検索APIのパラメータを作成する処理
    const currentParams = computed(() => {
      const params = route.path.replace('/search/hospital/list/', '')
      const resObj = getSearchQuery(params)

      if (resObj.free_word) {
        const res: any = {}

        res.free_word = resObj.free_word
        res.sort = String(state.sortOption)
        res.row_cnt = state.rowCnt

        if (resObj.page_no) res.page_no = resObj.page_no

        return res
      }

      resObj.sort = String(state.sortOption)
      resObj.row_cnt = state.rowCnt
      return resObj
    })

    // 最後に実行した検索条件をCookieに保存
    const saveLastSearchCondition = () =>
      setSearchConditionCookie('lastSearchCondition', currentParams.value)

    const ajaxApp = axiosSetting('app', 'block')
    const getHospitals = () => {
      state.isLoading = true

      if (list.length > 0) {
        list.splice(0, list.length)
        state.totalCount = 0
        state.pageNum = 1
      }

      const params = currentParams.value
      if (
        store.state.searchCoditionSaveFrg &&
        store.state.isLogin &&
        store.state.isSearchSubmit
      ) {
        params.save = true
        store.commit('updateSearchSubmit', false)
      }

      ajaxApp
        .post('/api/v1/j_search', params)
        .then((res) => {
          res.data.data.forEach((item: any) => {
            list.push(item)
          })

          state.totalCount = Number(res.data.total_num)
          state.pageNum = Number(res.data.page_num)
        })
        .catch((err: any) => {
          console.log(err)
        })
        .then(() => {
          state.isLoading = false
        })
    }

    // 有料バナー表示病院を取得して表示
    const adBanners = reactive<AdBannerType[]>([])
    const getAdBanners = () => {
      // 配列に入っているバナーを削除
      adBanners.splice(0)

      interface Params {
        [key: string]: string | Array<string> | undefined
        opt_id: string
        prefectures?: Array<string>
      }

      const params: Params = {
        opt_id: '3' // opt_idは3で固定
      }
      // 都道府県が検索に含まれていればパラメータにセット
      if (
        currentParams.value.prefectures &&
        currentParams.value.prefectures.length > 0
      ) {
        params.prefectures = currentParams.value.prefectures
      }

      ajaxApp
        .get('/api/v1/banner', {
          params: params
        })
        .then((res) => {
          // 広告バナーがなければ終了
          if (res.data.length === 0) return

          // 返却された病院データの中からランダムで1つ抽出してセット
          const ads = res.data
          const randomIndex: number = Math.floor(Math.random() * ads.length)
          adBanners.push(ads[randomIndex])
        })
    }

    // 内部リンクを取得して表示
    const internalLinks = reactive<InternalLinksType>({ list: {} })

    // 内部リンクを表示するかどうか判定
    const isCreateInternalLinks = (params: InternalLinksReqType): boolean => {
      // フリーワードの場合
      if (params.free_word) return false

      // 都道府県の指定が2つ以上
      if (params.prefectures && params.prefectures.length >= 2) return false

      // その他の検索条件が2つ以上
      if (
        (params.prefectures && Object.keys(params).length >= 3) ||
        (!params.prefectures && Object.keys(params).length >= 2)
      )
        return false

      return true
    }

    const createInternalLinks = async () => {
      // 既存の内部リンクを削除
      internalLinks.list = {}

      const params = JSON.parse(JSON.stringify(currentParams.value))

      // 使用しないプロパティを削除
      if (typeof params.row_cnt !== 'undefined') delete params.row_cnt // 件数
      if (typeof params.sort !== 'undefined') delete params.sort // ソート設定
      if (typeof params.page_no !== 'undefined') delete params.page_no // ページNo
      if (typeof params.bed_num_hi !== 'undefined') delete params.bed_num_hi // 病床数上限値
      if (typeof params.doctor_num_hi !== 'undefined')
        delete params.doctor_num_hi // 医師数上限値
      if (typeof params.income_hi !== 'undefined') delete params.income_hi // 年収上限値
      if (typeof params.positions_num_hi !== 'undefined')
        delete params.positions_num_hi // 募集定員上限値
      if (typeof params.shifts_num_hi !== 'undefined')
        delete params.shifts_num_hi // 当直回数上限値

      if (!isCreateInternalLinks(params)) return false

      try {
        const res = await ajaxApp({
          method: 'post',
          url: '/api/v1/internal_link',
          data: params
        })
        if (Object.keys(res.data).length) internalLinks.list = res.data
      } catch (error) {
        console.error(error)
      }
    }

    // ページャーのページ遷移
    const movePage = (num: number) => {
      const path = route.path
      const regexp = /pg_\d*/g
      const matches = path.match(regexp)
      let nextPagePath = ''

      if (num > 1 && matches !== null)
        nextPagePath = path.replace(matches[0], `pg_${num}`)

      if (num === 1 && matches !== null)
        nextPagePath = path.replace(`/${matches[0]}`, '')

      if (num > 1 && matches === null) nextPagePath = `${path}/pg_${num}`

      if (num === 1 && matches === null) nextPagePath = path

      router.push(nextPagePath)
    }

    const changeSortOptions = (num: number) => {
      state.sortOption = num

      const path = route.path
      const regexp = /pg_\d*/g
      const matches = path.match(regexp)

      if (matches !== null) {
        router.push(path.replace(`${matches[0]}/`, ''))
      } else {
        getHospitals()
      }
    }

    watch(
      () => route.path,
      () => {
        if (
          route.name === 'HospitalsList' ||
          route.name === 'HospitalsListAll'
        ) {
          getHospitals()
          getAdBanners()
          createInternalLinks()
          saveLastSearchCondition()
          setMeta()
        }
      }
    )

    onMounted(() => {
      // 初回実行
      getHospitals()
      saveLastSearchCondition()
      getAdBanners()
      createInternalLinks()
      setMeta()

      document.addEventListener('scroll', (e) => {
        if (isBusy || isPC()) return
        isBusy = true
        setTimeout(() => {
          isBusy = false
        }, 20)

        if (window.scrollY > 180 && window.scrollY > lastScrollTop) {
          state.showSearchConditionSm = true
        } else {
          state.showSearchConditionSm = false
        }

        lastScrollTop = window.scrollY
      })
    })

    return {
      list,
      state,
      currentParams,
      movePage,
      changeSortOptions,
      adBanners,
      internalLinks
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';

.searchResult {
  display: flex;
  flex-direction: column;
  padding-bottom: 40px;

  @include mixins.media() {
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: center;
    padding-bottom: 80px;
  }
}

.searchResult_title {
  order: 2;
  margin-top: 113px;
  margin-bottom: 40px;

  @include mixins.media() {
    order: 1;
    margin-top: 0;
    width: 100%;
  }
}

.searchResult_contents {
  order: 3;
  padding-left: 16px;
  padding-right: 16px;

  @include mixins.media() {
    order: 2;
    max-width: 772px;
    width: 69.2%;
    padding-left: 20px;
    padding-right: 3.7%;
  }
}

.searchResult_condition {
  order: 1;
  position: sticky;
  top: 0;
  height: 0;

  @include mixins.media() {
    order: 3;
    position: static;
    max-width: 356px;
    width: 30.8%;
    padding-right: 20px;
    height: auto;
  }
}

.searchResult_ad {
  order: 4;
  margin-top: 40px;

  @include mixins.media() {
    margin-top: 30px;
  }
}

.searchResult_internalLinks {
  width: 100%;
  padding-top: 40px;
  padding-bottom: 40px;
  margin-bottom: -40px;
  order: 5;
  background-color: #f5f5f5;

  @include mixins.media() {
    padding-top: 48px;
    padding-bottom: 48px;
    margin-top: 90px;
    margin-bottom: -80px;
  }
}

.searchResultInformation {
  margin-bottom: 15px;
}

/* TODO: おそらくsticky実装が正。問題なければ削除する */
.condition-fixed {
  position: fixed;
  top: 0;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.08);
  width: 100vw;
  z-index: 999;
}

.pagerBlock {
  margin-top: 30px;
}
</style>
